/**
 * 模型选择弹窗组件
 * 用于选择图像、视频、音频模型
 */
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MODEL_MODE_NAMES } from '../canvas/nodes/modelConfigs';
import './ModelSelectionModal.css';

const ModelSelectionModal = ({ show, onHide, mode, models, onSelect }) => {
  // ESC 键关闭弹窗
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && show) {
        onHide();
      }
    };

    if (show) {
      window.addEventListener('keydown', handleEsc);
      // 阻止背景滚动
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [show, onHide]);

  // 处理遮罩层点击
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  }, [onHide]);

  // 处理模型选择
  const handleModelClick = useCallback((modelType) => {
    onSelect(modelType);
  }, [onSelect]);

  if (!show) return null;

  const modalContent = (
    <div
      className={`ModelSelectionModal-backdrop ${show ? 'show' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`ModelSelectionModal-dialog ${show ? 'show' : ''}`}>
        <div className="ModelSelectionModal-content">
          {/* 头部 */}
          <div className="ModelSelectionModal-header">
            <h2 className="ModelSelectionModal-title">
              选择{MODEL_MODE_NAMES[mode]}
            </h2>
            <button
              className="ModelSelectionModal-close"
              onClick={onHide}
              aria-label="关闭"
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* 模型列表 */}
          <div className="ModelSelectionModal-body">
            <div className="ModelSelectionModal-grid">
              {models.map((model) => (
                <div
                  key={model.type}
                  className={`ModelSelectionModal-card ModelSelectionModal-card-${mode}`}
                  onClick={() => handleModelClick(model.type)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleModelClick(model.type);
                    }
                  }}
                >
                  {/* 图标 */}
                  <div className="ModelSelectionModal-card-icon" style={{ color: model.color }}>
                    <i className={`bi ${model.icon}`}></i>
                  </div>

                  {/* 名称 */}
                  <h3 className="ModelSelectionModal-card-name">{model.name}</h3>

                  {/* 描述 */}
                  <p className="ModelSelectionModal-card-description">{model.description}</p>

                  {/* 特性标签 */}
                  {model.tags && model.tags.length > 0 && (
                    <div className="ModelSelectionModal-card-tags">
                      {model.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="ModelSelectionModal-card-tag"
                          style={{ borderColor: model.color, color: model.color }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 底部提示 */}
          <div className="ModelSelectionModal-footer">
            <p className="ModelSelectionModal-hint">
              <i className="bi bi-info-circle"></i>
              点击模型卡片将自动创建节点并连接
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // 使用 Portal 渲染到 body
  return createPortal(modalContent, document.body);
};

export default ModelSelectionModal;
